import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const LINE1 = ["Besitze", "die"];
const LINE2 = ["Skins,", "die"];
const LINE3 = ["keiner", "mehr", "kriegt."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.15 } },
};
const word = {
  hidden: { y: "110%" },
  show: { y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const MaskLine = ({ words, accentIndex }) => (
  <span className="block overflow-hidden">
    <motion.span variants={container} className="flex flex-wrap gap-x-[0.25em]">
      {words.map((w, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span variants={word} className="block" style={accentIndex === i ? { color: "#e2ff00" } : undefined}>
            {w}
          </motion.span>
        </span>
      ))}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen pt-[72px] overflow-hidden">
      <div className="absolute inset-0 grain-grid opacity-60" aria-hidden="true" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center min-h-[calc(100vh-72px)] py-16">
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-[#8b8b93] mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#ff0055] animate-pulse" />
            Verifizierte OG Accounts · seit Season 1
          </motion.div>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-display font-extrabold leading-[0.92] tracking-tight text-[13vw] sm:text-[10vw] lg:text-[5.4rem]"
          >
            <MaskLine words={LINE1} />
            <MaskLine words={LINE2} accentIndex={0} />
            <MaskLine words={LINE3} />
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-8 max-w-md text-[#8b8b93] text-base leading-relaxed"
          >
            Ein handverlesener Tresor der seltensten OG Fortnite Skins. Jeder Kauf ist verifiziert und wird direkt an deinen Epic-Games-Account geliefert.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta"
              onClick={() => document.getElementById("vault")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-2 bg-[#e2ff00] text-black font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full hover:gap-4 transition-all"
            >
              Zum Tresor
              <ArrowDownRight size={16} className="group-hover:rotate-45 transition-transform" />
            </button>
            <div className="font-mono text-xs text-[#8b8b93]">
              <span className="text-white text-lg font-display">8</span> Grails verfügbar
            </div>
          </motion.div>
        </div>

        <motion.div style={{ opacity: fade }} className="relative h-[420px] sm:h-[560px] lg:h-[640px]">
          <div className="absolute inset-0 rounded-[2px] overflow-hidden border border-white/10" style={{ boxShadow: "0 40px 120px -30px rgba(255,0,85,0.35)" }}>
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src="/products/ghoul.jpg"
              alt="Featured OG Skin"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
          </div>
          <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#ff0055]">FEATURED · LIMITED</div>
              <div className="font-display font-bold text-2xl mt-1">OG Ghoul Pack</div>
            </div>
            <div className="font-mono text-xs text-white/70 backdrop-blur-md bg-black/40 border border-white/10 px-3 py-1.5 rounded-full">
              Verifiziert 2017
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
